import { ChangeEvent, FC, SyntheticEvent, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel, MenuItem } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { StandBuild } from '@pages/stands/types';
import {
    selectGetBuildDetailsErrorInfo,
    selectGetBuildDetailsStatus,
    selectGetBuildsForDeployStatus,
} from '@pages/stands/store/selectors';
import { DeployFiltersError, RequestStatusEnum, SelectedFilter } from '@shared/types';
import { getBuildDetailsRequest, getBuildDetailsReset, getBuildsForDeployRequest } from '@pages/stands/store';
import BranchSelector from '@pages/stands/components/DeployModal/BranchSelector';
import BuildSelector from '@pages/stands/components/DeployModal/BuildSelector';
import UtmsSelect from '@shared/components/ui/UtmsSelect';

interface DeployFiltersProps {
    selectedFilter: SelectedFilter;
    setSelectedFilter: (filter: SelectedFilter | ((prev: SelectedFilter) => SelectedFilter)) => void;
    buildDetails: SelectedFilter['build_details'][];
    error?: DeployFiltersError;
    showArchitecture?: boolean;
    showIsPublic?: boolean;
    disabledBranch?: boolean;
    disabledBuild?: boolean;
}

const DeployFilters: FC<DeployFiltersProps> = ({
    selectedFilter,
    setSelectedFilter,
    buildDetails,
    error,
    showArchitecture = false,
    showIsPublic = false,
    disabledBranch,
    disabledBuild,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const getBuildDetailsStatus = useSelector(selectGetBuildDetailsStatus);
    const getBuildsForDeployStatus = useSelector(selectGetBuildsForDeployStatus);

    const handleClearArchitecture = () => {
        setSelectedFilter((prev) => ({ ...prev, build_details: { architecture: '' } }));
    };

    const handleArchitectureChange = useCallback(
        (value: SelectedFilter['build_details']) => () => {
            let initialIsPublicValue = false;

            if (value.types && value.types.length === 1) {
                initialIsPublicValue = value.types[0] !== 'dev';
            }

            setSelectedFilter((filter) => ({
                ...filter,
                build_details: value,
                is_public: initialIsPublicValue,
            }));
        },
        [setSelectedFilter],
    );

    const handleIsPublicChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSelectedFilter((filter) => ({
            ...filter,
            is_public: event.target.checked,
        }));
    };

    const handleClearBuild = () => {
        setSelectedFilter((prev) => ({ ...prev, build: '' }));
        handleClearArchitecture();
    };

    const handleBuildChange = (_event: SyntheticEvent<Element, Event>, value: StandBuild | null) => {
        if (!value) return;
        setSelectedFilter((prev) => ({ ...prev, build: value }));
        handleClearArchitecture();
    };

    const handleBranchChange = (branch: string) => {
        setSelectedFilter((prev) => ({ ...prev, branch }));
        handleClearBuild();
        handleClearArchitecture();
    };

    useEffect(() => {
        if (
            selectedFilter.branch &&
            getBuildDetailsStatus === RequestStatusEnum.Success &&
            buildDetails?.length &&
            !selectedFilter.build_details?.architecture
        ) {
            const newBuildDetails =
                buildDetails.length === 1 ? buildDetails[0] : buildDetails.find((item) => item.architecture === 'x86');
            newBuildDetails && handleArchitectureChange(newBuildDetails)();
        }
    }, [buildDetails, getBuildDetailsStatus, handleArchitectureChange, selectedFilter]);

    useEffect(() => {
        if (!selectedFilter.branch) {
            return;
        }

        dispatch(getBuildsForDeployRequest(selectedFilter.branch));
    }, [dispatch, selectedFilter.branch]);

    useEffect(() => {
        if (selectedFilter.branch && !selectedFilter.build && getBuildsForDeployStatus === RequestStatusEnum.Success) {
            setSelectedFilter((prev) => ({ ...prev, build: 'last' }));
        }
    }, [getBuildsForDeployStatus, setSelectedFilter, selectedFilter]);

    useEffect(() => {
        if (!(selectedFilter.branch && selectedFilter.build)) {
            return;
        }

        dispatch(getBuildDetailsRequest({ branch: selectedFilter.branch, build: selectedFilter.build }));
    }, [dispatch, selectedFilter.branch, selectedFilter.build]);

    return (
        <>
            <BranchSelector
                disabled={disabledBranch}
                error={error === 'branch'}
                value={selectedFilter.branch}
                onChange={handleBranchChange}
            />
            <BuildSelector
                value={selectedFilter.build}
                onChange={handleBuildChange}
                disabled={disabledBuild}
                error={error === 'build'}
            />
            {showArchitecture && (
                <UtmsSelect
                    id="architecture"
                    label={t('architecture')}
                    value={selectedFilter.build_details?.architecture}
                    options={buildDetails.map((item) => ({ label: item.architecture, value: item }))}
                    renderValue={(option) => (
                        <MenuItem
                            key={`${option.value.architecture}_${option.value.types}`}
                            onClick={handleArchitectureChange(option.value)}
                            value={option.value.architecture}
                        >
                            {option.label}
                        </MenuItem>
                    )}
                    error={error === 'architecture'}
                    errorMessage={t('specify_architecture')}
                    formControlProps={{
                        disabled: !selectedFilter.build,
                        sx: { width: 160 },
                    }}
                    objectForFetch={{
                        getOptionsStatus: getBuildDetailsStatus,
                        getOptionsReset: getBuildDetailsReset,
                        errorSelector: selectGetBuildDetailsErrorInfo,
                        objectLabel: t('architecture').toLowerCase(),
                    }}
                />
            )}
            {showIsPublic && (
                <FormControlLabel
                    disabled={!selectedFilter.build_details?.types || selectedFilter.build_details?.types.length < 2}
                    control={<Checkbox checked={selectedFilter.is_public} onChange={handleIsPublicChange} />}
                    label={t('public')}
                />
            )}
        </>
    );
};

export default DeployFilters;
